import React from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../../store';
import { useTranslation } from 'react-i18next';
import { Address } from '../../common/address';
import { ErrorType, SalesforceFieldMaxLength } from '../../../domains/common.domain';
import { CheckboxComponent } from 'n2p-ui-library';
import { ContactForm } from '../../common/contact-form';
import classNames from 'classnames';
import SelectInput from '../../common/inputs/select-input';
import StringInput from '../../common/inputs/string-input';

const ShippingInformation = observer(() => {
  const { t } = useTranslation('', { keyPrefix: 'SHIPPING_INFORMATION' });
  const { t: tError } = useTranslation('', { keyPrefix: 'SHIPPING_INFORMATION.ERRORS' });
  const { t: tRestrictions } = useTranslation('', { keyPrefix: 'SHIPPING_INFORMATION.TIME_RESTRICTIONS' });

  const { shippingAddressStore, shippingContactStore, shippingInformationStore: store, quoteStore, tenantStore } = useStore();
  const { address } = shippingAddressStore;
  const { contacts } = shippingContactStore;
  const { information, isShippingCalculated, isRecalculating } = store;
  const { displayTimeRestriction } = tenantStore.config;

  const handleAddressChange = async (field: keyof typeof shippingAddressStore.address, value: string) => {
    await store.updateAddress(field, value);
  };

  const setAddressError = (field: keyof typeof shippingAddressStore.address, error: ErrorType) => {
    shippingAddressStore.setError(field, error);
  };

  return (
    <div className={'step'}>
      <h2>{t('TITLE')}</h2>
      <span className={'step-info'}>{t('INFO')}</span>

      {quoteStore.isServiceStepAvailable && (
        <div className={'container-same-as'}>
          <CheckboxComponent
            label={t('SAME_AS_SERVICE_ADDRESS')}
            onChange={e => store.updateSameAsServiceAddress()}
            checked={store.isSameAsServiceAddress}
          />
        </div>
      )}
      <Address
        address={address}
        handleChange={handleAddressChange}
        setError={setAddressError}
        selectedCountry={shippingAddressStore.selectedCountry}
        showRecipient={true}
        onCityNormalized={city => shippingAddressStore.updateAddress('city', city)}
      />

      {quoteStore.displayShippingOptions && (
        <div className={'container-recalculate container-grey'}>
          <h4>{t('GET_SHIPPING')}</h4>
          <span>{t('CLICK_RECALCULATE')}</span>
          <div className={'container-two-columns'}>
            <SelectInput
              value={information.shippingMethod}
              label={t('SELECT_METHOD')}
              customError={
                information.shippingMethod.errorType ? tError(`${information.shippingMethod.errorType.toUpperCase()}_SHIPPING_METHOD`) : ''
              }
              warning={
                information.shippingMethod.warningType
                  ? tError(`${information.shippingMethod.warningType.toUpperCase()}_SHIPPING_METHOD`)
                  : ''
              }
              onUpdate={value => store.updateInformation('shippingMethod', value)}
              selectValues={store.shippingOptions.map(option => ({ label: option.name, value: option.value }))}
            />
            <button
              type="button"
              className={classNames('n2pc-btn-primary action-btn recalculate-btn', isRecalculating && 'loading')}
              disabled={isShippingCalculated || isRecalculating}
              onClick={() => store.recalculateShipping()}
            >
              {t('RECALCULATE')}
            </button>
          </div>
        </div>
      )}

      {displayTimeRestriction && (
        <>
          <h3>{tRestrictions('TITLE')}</h3>
          <span className={'step-info m-bottom-0'}>{tRestrictions('DESCRIPTION')}</span>
          <StringInput
            value={information.timeRestriction}
            name={'timeRestriction'}
            label={''}
            onUpdate={value => store.updateInformation('timeRestriction', value)}
            error={''}
            maxLength={SalesforceFieldMaxLength}
            placeholder={tRestrictions('PLACEHOLDER')}
          />
        </>
      )}

      <h3>{t('SHIPPING_CONTACT')}</h3>
      <span className={'step-info'}>{t('SHIPPING_CONTACT_INFO')}</span>
      {!quoteStore.isContactOnlyPartner && (
        <div className={'container-same-as'}>
          <CheckboxComponent
            label={t('SAME_AS_ONBOARDING_CONTACT')}
            onChange={e => store.updateSameAsFirstOnboardingContact()}
            checked={store.isSameAsFirstOnboardingContact}
          />
        </div>
      )}
      {contacts.map((contact, index) => (
        <ContactForm
          key={contact.uiKey}
          index={index}
          contact={contact}
          handleChange={(field, value) => store.updateContact(field, value)}
          contactsType={'shipping'}
        />
      ))}
    </div>
  );
});

export default ShippingInformation;
